import {
  Box,
  Button,
  IconButton,
  Theme,
  Typography,
  alpha,
  keyframes,
  styled,
} from '@mui/material';

import { GOLD_ACCENT } from 'src/components/buyCreditsDialog/styles/index.styles';

/**
 * The Pro pass: a light sheet carrying two accents, and only two — gold is what
 * the pass grants, blue is the platform it grants it on. The header, price and
 * CTA of the career pitch; the "who this is for" list is in styles.tsx.
 *
 * Surfaces and body text come from the theme, so the sheet follows the app into
 * dark mode; the golds are fixed, because gold that shifts isn't gold.
 */

export const GOLD_LIGHT = '#FFDF8A';
export const GOLD_DEEP = '#E0A82E';
/** Gold dark enough to read as text on white and on the gold-tinted rows
 * (about 5:1) — the accent itself is far too pale. */
export const GOLD_TEXT = '#8A6000';
/**
 * The sheet's small explanatory text in light mode. The theme's secondary grey
 * reads at about 4.2:1 on white and under 4:1 on the tinted panels, below AA
 * at these 10-13px sizes; this is about 6:1 and 5.5:1. Dark mode keeps the
 * theme's, which already clears it on the dark paper.
 */
const BODY_TEXT = '#5F6368';
export const bodyText = (theme: Theme) => ({
  color: BODY_TEXT,
  ...theme.applyStyles('dark', { color: theme.palette.text.secondary }),
});
/** Ink for text sitting on gold — the only shade legible at label size. */
export const GOLD_INK = '#3A2C00';

const shine = keyframes`
  0% { transform: translateX(-130%) skewX(-18deg); }
  60%, 100% { transform: translateX(320%) skewX(-18deg); }
`;

const stillness = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};

export const Shell = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  // Full height by default; the pitch sets its own (content-sized on a desktop,
  // full screen on a phone).
  height: '100%',
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
  // A breath of gold from the top and blue from the bottom — the two things the
  // pass is about, before a word is read.
  backgroundImage: `radial-gradient(110% 70% at 50% -8%, ${alpha(GOLD_ACCENT, 0.22)} 0%, transparent 60%), radial-gradient(90% 60% at 50% 108%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 60%)`,
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 3,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
  },
}));

// ─── Header ──────────────────────────────────────────────────────────────────

export const HeadBox = styled(Box)({
  flexShrink: 0,
  padding: '22px 24px 4px',
  '@media (max-width: 600px)': { padding: '20px 18px 4px' },
});

export const ProChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '4px 12px 4px 9px',
  borderRadius: 999,
  border: `1px solid ${alpha(GOLD_DEEP, 0.4)}`,
  backgroundColor: alpha(GOLD_ACCENT, 0.18),
  color: GOLD_TEXT,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  // GOLD_TEXT is dark enough for white and all but vanishes on the dark paper.
  ...theme.applyStyles('dark', { color: theme.palette.brand.goldInk }),
}));

export const PitchTitle = styled(Typography)(({ theme }) => ({
  marginTop: '11px',
  fontSize: 24,
  fontWeight: 800,
  lineHeight: 1.12,
  letterSpacing: '-0.025em',
  color: theme.palette.primary.main,
  '@media (max-width: 600px)': { fontSize: 21 },
}));

// ─── Price + CTA ─────────────────────────────────────────────────────────────

export const CtaFooter = styled(Box)({
  flexShrink: 0,
  padding: '14px 24px 16px',
  '@media (max-width: 600px)': {
    padding: '12px 18px calc(14px + env(safe-area-inset-bottom))',
  },
});

export const PriceRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 10,
});

export const PriceMain = styled(Typography)(({ theme }) => ({
  fontSize: 28,
  fontWeight: 800,
  lineHeight: 1,
  letterSpacing: '-0.03em',
  color: theme.palette.primary.main,
}));

export const PriceTerm = styled(Typography)(({ theme }) => ({
  fontSize: 12.5,
  fontWeight: 500,
  ...bodyText(theme),
}));

export const PriceStrike = styled(Typography)(({ theme }) => ({
  fontSize: 13.5,
  fontWeight: 700,
  color: theme.palette.text.disabled,
  textDecoration: 'line-through',
}));

export const DiscountPill = styled(Box)({
  padding: '3px 9px',
  borderRadius: 999,
  backgroundColor: GOLD_ACCENT,
  color: GOLD_INK,
  fontSize: 11,
  fontWeight: 800,
});

export const GoProCta = styled(Button)({
  '&&&': {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    height: 52,
    borderRadius: 15,
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 800,
    letterSpacing: '0.01em',
    color: GOLD_INK,
    background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD_ACCENT} 45%, ${GOLD_DEEP})`,
    boxShadow: `0 10px 26px ${alpha(GOLD_DEEP, 0.42)}`,
    transition: 'transform .16s ease, box-shadow .16s ease',
    '&:hover': {
      background: `linear-gradient(135deg, ${GOLD_LIGHT}, ${GOLD_ACCENT} 45%, ${GOLD_DEEP})`,
      transform: 'translateY(-1px)',
      boxShadow: `0 14px 32px ${alpha(GOLD_DEEP, 0.5)}`,
    },
    '&:active': { transform: 'translateY(0)' },
    '&.Mui-disabled': { color: alpha(GOLD_INK, 0.5) },
    // Light raking across the button — the only thing on the screen that moves
    // on its own, and it sits on the thing we want pressed.
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 60,
      background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.6)}, transparent)`,
      animation: `${shine} 3.8s ease-in-out infinite`,
      ...stillness,
    },
  },
});

export const FootNote = styled(Typography)(({ theme }) => ({
  marginTop: '9px',
  fontSize: 11.5,
  fontWeight: 400,
  ...bodyText(theme),
  textAlign: 'center',
}));

export const TopUpLink = styled(Button)(({ theme }) => ({
  '&&&': {
    display: 'block',
    margin: '4px auto 0',
    padding: '4px 8px',
    minWidth: 0,
    textTransform: 'none',
    fontSize: 12.5,
    fontWeight: 700,
    color: theme.palette.primary.main,
    background: 'none',
    '&:hover': { background: 'none', textDecoration: 'underline' },
  },
}));

/** The terms link, inline at the end of the footnote's line. */
export const termsLinkSx = {
  font: 'inherit',
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
} as const;
